import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { BOARD_TEMPLATES, type BoardTemplate } from "@/lib/boardTemplates";
import type { Board, BoardCreatePayload, UUID } from "@/lib/types";

interface Vars {
  workspaceId: UUID;
  payload: BoardCreatePayload;
  template?: BoardTemplate;
}

export function useCreateBoard() {
  const qc = useQueryClient();
  return useMutation<Board, Error, Vars>({
    mutationFn: async ({ workspaceId, payload, template }) => {
      const tplKind = template ? BOARD_TEMPLATES[template].kind : "project";
      const board = await api.createBoard(workspaceId, {
        ...payload,
        kind: payload.kind ?? tplKind,
      });
      if (template) {
        const def = BOARD_TEMPLATES[template];
        if (def.columns.length > 0) {
          // Columns carry explicit position floats so concurrent inserts still
          // land in the right order. Parallel POST shaves wall-clock vs sequential.
          await Promise.all(def.columns.map((col) => api.createColumn(board.id, col)));
        }
      }
      return board;
    },
    onSuccess: (board) => {
      qc.setQueryData<Board[]>(queryKeys.boards(board.workspace_id), (prev) =>
        prev ? [board, ...prev] : [board],
      );
      void qc.invalidateQueries({ queryKey: queryKeys.boards(board.workspace_id) });
      void qc.invalidateQueries({ queryKey: queryKeys.myBoards });
      void qc.invalidateQueries({ queryKey: queryKeys.board(board.id) });
    },
  });
}
